<template>
  <v-navigation-drawer
    v-model="drawer"
    :mini-variant="drawerMini"
    :width="this.$vuetify.breakpoint.mdAndDown ? 256 : 300"
    class="fill-height pa-0 pb-4"
    :class="[{ 'custom-drawer': this.$vuetify.breakpoint.smAndDown }]"
    color="grey lighten-3"
    left
    floating
    permanent
  >
    <v-list-item
      class="d-flex pt-4" 
      :class="drawerMini ? 'justify-center' : 'justify-end pr-2'"
    >
      <v-btn
        @click="drawerMini = !drawerMini"
        icon
      >
        <v-icon
        >{{ drawerMini ? 'mdi-arrow-collapse-right' : 'mdi-arrow-collapse-left' }}</v-icon>
      </v-btn>
    </v-list-item>

    <v-list-item
      class="d-flex px-3"
      :class="drawerMini ? 'justify-center' : 'justify-end'"
    >
      <v-btn
        @click="$emit('create-session'); clearSelectedSession()"
        class="rounded-pill mt-6 mb-2"
        color="primary"
        block
        elevation="0"
        :fab="drawerMini"
        :small="drawerMini"
      >
        <v-icon>mdi-plus</v-icon>{{ drawerMini ? '' : $t('create_new_session') }}
      </v-btn>
    </v-list-item>

    <v-list
      v-if="!drawerMini"
      class="pa-0 ma-0"
      flat
    >
      <template
        v-for="groups in userSessionsGroupedByTimeIntervals"
      >
        <template v-for="([sessionGroup, sessions]) in Object.entries(groups)">
          <v-list-group
            v-if="sessions.length > 0"
            :key="sessionGroup"
            :value="sessionGroup == 'this_week' ? true : false"
            :ripple="false"
          >
            <template v-slot:activator>
              <v-list-item-content>
                <v-list-item-title
                  class="text-subtitle-2 font-weight-regular"
                >{{ $t(sessionGroup) }}</v-list-item-title>
              </v-list-item-content>
            </template>

            <v-list-item-group
              v-model="selectedSession"
              color="primary"
            >
              <v-list-item
                v-for="session in sessions"
                class="pl-4 pr-2 py-1"
                :key="session.session_id"
                :value="session.session_id"
                @click.stop="$emit('selected-session', session.session_id)"
                dense
              >
                <v-list-item-content>
                  <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                      <v-list-item-title
                        class="text-body-2"
                        v-bind="attrs"
                        v-on="on"
                      >{{ session.title || $t('user_inquiry') }}</v-list-item-title>
                    </template>

                    <span>{{ session.title || $t('user_inquiry') }}</span>
                  </v-tooltip>
                </v-list-item-content>

                <v-list-item-action class="ml-1 my-0 align-self-center">
                  <v-menu
                    bottom
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        icon
                      >
                        <v-icon size="22">mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>

                    <v-list min-width="187">
                      <v-list-item
                        @click="openRenameSessionModal(session)"
                        class="pl-5"
                      >
                        <v-list-item-icon class="mr-3">
                          <v-icon>mdi-pencil-outline</v-icon>
                        </v-list-item-icon>

                        <v-list-item-content>
                          <v-list-item-title>{{ $t('rename') }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item
                        @click="openRemoveSessionModal(session)"
                        class="pl-5"
                      >
                        <v-list-item-icon class="mr-3">
                          <v-icon>mdi-delete-outline</v-icon>
                        </v-list-item-icon>

                        <v-list-item-content>
                          <v-list-item-title>{{ $t('delete') }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-list-item-action>
              </v-list-item>
            </v-list-item-group>
          </v-list-group>
        </template>
      </template>

      <v-list-item
        v-if="sessions.length === 0"
        class="justify-center"
        @click="clearSelectedSession()"
      >
        <v-icon class="mr-2" size="22">mdi-message-alert-outline</v-icon>
        <v-subheader>{{ $t('no_sessions') }}</v-subheader>
      </v-list-item>

      <v-list-item
        v-else
        class="justify-center"
        @click="$emit('load-more-sessions')"
        :disabled="loading"
      >
          <v-progress-circular
            v-if="loading"
            indeterminate
            width="2"
            color="primary"
            size="22"
            class="mr-2"
          ></v-progress-circular>

          <v-icon v-else>mdi-reload</v-icon>
        <v-subheader>{{ $t('load_more') }}</v-subheader>
      </v-list-item>
    </v-list>

    <DefaultModal
      @submit="renameSession"
      :title="$t('rename')"
      submit-button-text="rename"
      ref="modalRename"
    >
      <template v-slot:content>
        <v-text-field
          v-if="actionSession"
          :placeholder="actionSession.title || ''"
          :rules="[
            v => !!v || $t('form_rules.mandatory'),
            v => /^([a-zA-Z0-9À-ž\s\._-]+)$/igu.test(v) || $t('form_rules.invalid_filename'),
          ]"
          name="title"
          outlined
          dense
        ></v-text-field>
      </template>
    </DefaultModal>

    <DefaultModal
      :title="$t('remove')"
      ref="modalRemove"
      @submit="removeSession"
      submit-button-text="remove"
      deleteModal
    >
      <template v-slot:content>
        <span v-if="actionSession">{{ $t('remove_entry_confirmation') }} <b>{{ actionSession.title || '' }}</b></span> ?
      </template>
    </DefaultModal>
  </v-navigation-drawer>
</template>

<style scoped>
  .v-list-group--active {
    color: inherit !important;
  }

  .custom-drawer {
    position: absolute;
  }
  
  .custom-drawer:not(.v-navigation-drawer--mini-variant) {
    box-shadow:
      0px 8px 10px -5px rgba(0, 0, 0, 0.2),
      0px 16px 24px 2px rgba(0, 0, 0, 0.14),
      0px 6px 30px 5px rgba(0, 0, 0, 0.12),
      0px 6px 3000px 5000px rgba(0, 0, 0, 0.3);
  }
</style>

<script>
  import HumanReadable from "@/utils/human_readable"

  import DefaultModal from "@/components/DefaultModal.vue"

  export default {
    name: 'UserSessionsDrawer',

    components: {
      DefaultModal,
    },

    props: {
      loading: {
        type: Boolean,
        default: false,
      },
      sessions: {
        type: Array,
        default: () => [],
      },
    },

    data() {
      return {
        drawer: true,
        drawerMini: this.$vuetify.breakpoint.lgAndDown,
        selectedSession: null,
        actionSession: null,
      }
    },

    computed: {
      userSessionsGroupedByTimeIntervals() {
        const groupedSessions = {
          thisWeek: {
            'this_week': [],
          },
          thisMonth: {
            'this_month': [],
          },
          thisYearMonths: [],
          olderYears: [],
        }

        const now = new Date()
        const thisWeek = new Date(now - 7 * 24 * 60 * 60 * 1000)
        const thisMonth = new Date(now - 30 * 24 * 60 * 60 * 1000)
        const thisYear = now.getFullYear()

        this.sessions.forEach((session) => {
          const sessionDate = new Date(session.created_at)

          if (sessionDate >= thisWeek) {
            groupedSessions.thisWeek['this_week'].push(session)
          } else if (sessionDate >= thisMonth) {
            groupedSessions.thisMonth['this_month'].push(session)
          } else if (sessionDate.getFullYear() === thisYear) {
            let monthName = HumanReadable.getMonthName(sessionDate.getMonth())

            if (!groupedSessions.thisYearMonths[monthName]) {
              groupedSessions.thisYearMonths[monthName] = []
            }

            groupedSessions.thisYearMonths[monthName].push(session)
          } else {
            if (!groupedSessions.olderYears[sessionDate.getFullYear()]) {
              groupedSessions.olderYears[sessionDate.getFullYear()] = []
            }

            groupedSessions.olderYears[sessionDate.getFullYear()].push(session)
          }
        })

        return groupedSessions
      },
    },

    methods: {
      openRenameSessionModal(session) {
        this.actionSession = {...session}
        this.$refs.modalRename.openModal()
      },

      openRemoveSessionModal(session) {
        this.actionSession = {...session}
        this.$refs.modalRemove.openModal()
      },

      renameSession(formData) {
        this.$refs.modalRename.closeModal()
        this.$emit('rename-session', {...this.actionSession, title: formData.get('title')})
        this.actionSession = null
      },

      removeSession() {
        this.$refs.modalRemove.closeModal()
        this.$emit('remove-session', {...this.actionSession})
        this.actionSession = null
      },

      setSelectedSession(sessionId) {
        this.selectedSession = sessionId
      },

      clearSelectedSession() {
        this.selectedSession = null
      },

      collapseDrawer() {
        this.drawerMini = true
      },
    },
  }
</script>
